// tktuner login — RFC-8628-style device flow against the TkTuner API.
import { spawn } from "node:child_process";
import { hostname, userInfo } from "node:os";
import pc from "picocolors";
import { apiCall, apiRequest } from "../lib/api-client";
import { readCredentials, writeCredentials } from "../lib/credentials";
import { CLI_VERSION } from "../lib/version";

export const DEFAULT_API_URL = "https://tktuner.com";

export interface LoginOptions {
  api?: string;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Best-effort browser open — the URL is ALWAYS printed (WSL2 often can't open). */
function openBrowser(url: string): void {
  const candidates: string[][] =
    process.platform === "darwin"
      ? [["open", url]]
      : process.platform === "win32"
        ? [["cmd", "/c", "start", "", url]]
        : [
            ["wslview", url],
            ["xdg-open", url],
          ];
  for (const [cmd, ...args] of candidates) {
    try {
      const child = spawn(cmd as string, args, { stdio: "ignore", detached: true });
      child.on("error", () => {});
      child.unref();
      return;
    } catch {
      // try the next opener
    }
  }
}

export async function login(opts: LoginOptions): Promise<void> {
  const existing = readCredentials();
  if (existing) {
    console.log(
      `Already logged in (device ${pc.bold(existing.deviceName)} → ${existing.apiUrl}).\n` +
        `Run ${pc.bold("tktuner logout")} first to switch accounts.`,
    );
    return;
  }
  const apiUrl = opts.api ?? DEFAULT_API_URL;
  const deviceName = `${userInfo().username}@${hostname()}`.slice(0, 80);

  const start = await apiCall("POST", apiUrl, "/v1/device/code", {
    body: { deviceName, cliVersion: CLI_VERSION },
  });
  const deviceCode = start.device_code as string;
  const userCode = start.user_code as string;
  const verifyUrl = (start.verification_uri_complete ?? start.verification_uri) as string;
  let intervalMs = ((start.interval as number) ?? 5) * 1000;
  const deadline = Date.now() + ((start.expires_in as number) ?? 900) * 1000;

  console.log(`\nOpen ${pc.bold(pc.cyan(verifyUrl))}`);
  console.log(`and confirm this code: ${pc.bold(userCode)}\n`);
  openBrowser(verifyUrl);
  process.stdout.write("Waiting for approval");

  while (Date.now() < deadline) {
    await sleep(intervalMs);
    process.stdout.write(".");
    const { status, json } = await apiRequest("POST", apiUrl, "/v1/device/token", {
      body: { device_code: deviceCode },
    });
    if (status === 200) {
      writeCredentials({
        apiUrl,
        deviceToken: json.device_token as string,
        deviceId: json.device_id as string,
        deviceName,
      });
      console.log(`\n\n${pc.green("Logged in.")} Device ${pc.bold(deviceName)} is connected.`);
      console.log(`Run ${pc.bold("tktuner sync")} to upload your usage metadata.`);
      return;
    }
    if (status === 202) continue;
    const code = ((json.error ?? {}) as { code?: string }).code;
    if (code === "SLOW_DOWN") {
      intervalMs += 2000;
      continue;
    }
    const message = ((json.error ?? {}) as { message?: string }).message ?? `HTTP ${status}`;
    console.error(`\n${pc.red("Login failed:")} ${message}`);
    process.exitCode = 1;
    return;
  }
  console.error(`\n${pc.red("Login timed out.")} Run tktuner login again.`);
  process.exitCode = 1;
}
